import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, inject, signal } from '@angular/core'

import { ViiaSyncAccountTransactionsJobData } from '@lemon/core-firebase/model/aiia'
import { Asiakas } from '../../../_jaettu-lemonator/model/asiakas'

import { AsiakasService } from '../../../_angular/service/asiakas/asiakas.service'

import { FieldTree, form, required } from '@angular/forms/signals'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { combineLatest, Observable, of as observableof, Subject } from 'rxjs'
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators'

interface ViiaSyncAccountTransactionsJobDataForDisplay extends ViiaSyncAccountTransactionsJobData {
  lastDelayReason: string
  uri: string
}

interface HistoriaLomake {
  historianMaara: string | null
}

@Component({
  selector: '[app-aiia-tyohistoriat]',
  templateUrl: './aiia-tyot.component.html',
  styleUrls: ['./aiia-tyot.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AiiaTyotComponent implements OnInit, OnDestroy {
  asiakas: Asiakas = null

  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  historiaObservable: Observable<ViiaSyncAccountTransactionsJobDataForDisplay[]>

  lomake: FieldTree<HistoriaLomake> = form(signal<HistoriaLomake>({ historianMaara: null }), polku => {
    required(polku.historianMaara)
  })

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    private _asiakasService: AsiakasService,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {}

  ngOnInit() {
    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(result => {
      this.asiakas = result
    })

    const uri = 'tyojono/viia/viia-update-transactions-jobs'
    this.historiaObservable = combineLatest([
      this._asiakasService.nykyinenAsiakasAvainObservable,
      arvonMuutokset(this.lomake.historianMaara, { injector: this._injector }).pipe(startWith('50')),
      this.kirjanpitajaOnDevaajaObservable,
    ]).pipe(
      switchMap(([asiakas, historianMaara, onDevaaja]) => {
        if (!asiakas) {
          return observableof<ViiaSyncAccountTransactionsJobDataForDisplay[]>([])
        }
        return this._firebaseLemonaid
          .firestoreCollection<ViiaSyncAccountTransactionsJobDataForDisplay>(uri)
          .where('asiakasAvain', '==', asiakas.avain)
          .limit(Number(historianMaara))
          .listenSnapshots()
          .pipe(
            map(res =>
              res.map(snap => {
                const a = snap.data()
                if (a.delayedReasons?.length > 0) {
                  a.lastDelayReason = JSON.stringify(a.delayedReasons[a.delayedReasons.length - 1])
                } else {
                  a.lastDelayReason = 'Not delayed yet'
                }
                if (onDevaaja) {
                  a.uri = this._debugService.createFirestoreLinkLemonaid(uri + '/' + snap.id)
                }
                return a
              }),
            ),
            map(res => res.sort((a, b) => a.delayedTimes - b.delayedTimes)),
          )
      }),
    )

    asetaArvo(this.lomake.historianMaara, '50')
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
